import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, Clock, MapPin, Trophy, Save, X, Users, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

interface GameEditFormProps {
  gameData: any;
  onSave: (updatedData: any) => Promise<void>;
  onCancel: () => void;
  userPermissions: {
    canEditBasicInfo: boolean;
    canEditTeams: boolean;
    canEditSettings: boolean;
    canEditApprovalStatus: boolean;
    role: string;
  };
}

interface Team {
  team_id: number;
  team_name: string;
  team_logo?: string;
}

interface Sport {
  id: number;
  sport_name: string;
}

export const GameEditForm: React.FC<GameEditFormProps> = ({
  gameData,
  onSave,
  onCancel,
  userPermissions,
}) => {
  const [formData, setFormData] = useState({
    game_name: gameData.game_name || gameData.game_title || '',
    game_title: gameData.game_title || gameData.game_name || '',
    game_description: gameData.game_description || '',
    game_date: gameData.game_date || '',
    game_time: gameData.game_time || '',
    field_location: gameData.field_location || '',
    venue_name: gameData.venue_name || '',
    venue_address: gameData.venue_address || '',
    sport_id: gameData.sport_id || null,
    home_team_id: gameData.home_team_id || null,
    away_team_id: gameData.away_team_id || null,
    scorekeeper_needed: gameData.scorekeeper_needed || false,
    scorekeeper_points: gameData.scorekeeper_points || 50,
    game_status: gameData.game_status || gameData.status || 'pending',
    home_team_status: gameData.home_team_status || 'pending',
    away_team_status: gameData.away_team_status || 'pending',
  });

  const [teams, setTeams] = useState<Team[]>([]);
  const [sports, setSports] = useState<Sport[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    loadFormData();
  }, []);

  const loadFormData = async () => {
    try {
      setLoadingData(true);
      
      // Load teams and sports
      const [teamsResponse, sportsResponse] = await Promise.all([
        supabase.from('teams').select('team_id, team_name, team_logo').order('team_name'),
        supabase.from('available_sports').select('id, sport_name').order('sport_name')
      ]);

      if (teamsResponse.data) setTeams(teamsResponse.data);
      if (sportsResponse.data) setSports(sportsResponse.data);
      
    } catch (error) {
      console.error('Error loading form data:', error);
      toast.error('Failed to load form data');
    } finally {
      setLoadingData(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      // Validate required fields
      if (!formData.game_name && !formData.game_title) {
        toast.error('Game title is required');
        return;
      }
      
      if (!formData.game_date) {
        toast.error('Game date is required');
        return;
      }
      
      if (!formData.game_time) {
        toast.error('Game time is required');
        return;
      }

      // Prepare update data with proper field mapping
      const updateData: any = {
        game_name: formData.game_name || formData.game_title,
        game_title: formData.game_title || formData.game_name,
        game_description: formData.game_description,
        game_date: formData.game_date,
        game_time: formData.game_time,
        field_location: formData.field_location,
        venue_name: formData.venue_name,
        venue_address: formData.venue_address,
        sport_id: formData.sport_id,
        home_team_id: formData.home_team_id,
        away_team_id: formData.away_team_id,
        scorekeeper_needed: formData.scorekeeper_needed,
        scorekeeper_points: formData.scorekeeper_points,
        home_team_status: formData.home_team_status,
        away_team_status: formData.away_team_status,
        updated_at: new Date().toISOString()
      };

      // Only include status if user has permission
      if (userPermissions.canEditApprovalStatus) {
        updateData.game_status = formData.game_status;
        updateData.status = formData.game_status;
      }

      await onSave(updateData);
      toast.success('Game updated successfully');
      
    } catch (error) {
      console.error('Error updating game:', error);
      toast.error('Failed to update game');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-muted rounded w-48"></div>
        <div className="h-64 bg-muted rounded"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Edit Game</h2>
          <p className="text-gray-400">Update game details and settings</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} disabled={loading}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-sports-green text-black hover:bg-green-600">
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Form Tabs */}
      <Tabs defaultValue="basic" className="w-full">
        <TabsList className="grid w-full grid-cols-3 bg-gray-800">
          <TabsTrigger value="basic" className="text-gray-300 data-[state=active]:bg-gray-700 data-[state=active]:text-white">
            <Trophy className="h-4 w-4 mr-2" />
            Basic Info
          </TabsTrigger>
          <TabsTrigger value="teams" className="text-gray-300 data-[state=active]:bg-gray-700 data-[state=active]:text-white">
            <Users className="h-4 w-4 mr-2" />
            Teams
          </TabsTrigger>
          <TabsTrigger value="settings" className="text-gray-300 data-[state=active]:bg-gray-700 data-[state=active]:text-white">
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </TabsTrigger>
        </TabsList>

        {/* Basic Information Tab */}
        <TabsContent value="basic" className="space-y-4">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Trophy className="h-5 w-5" />
                Game Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {userPermissions.canEditBasicInfo ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="game_title" className="text-white">Game Title *</Label>
                      <Input
                        id="game_title"
                        value={formData.game_title}
                        onChange={(e) => handleInputChange('game_title', e.target.value)}
                        placeholder="Enter game title"
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="sport_id" className="text-white">Sport</Label>
                      <Select value={formData.sport_id?.toString() || ''} onValueChange={(value) => handleInputChange('sport_id', value ? parseInt(value) : null)}>
                        <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                          <SelectValue placeholder="Select sport" />
                        </SelectTrigger>
                        <SelectContent>
                          {sports.map((sport) => (
                            <SelectItem key={sport.id} value={sport.id.toString()}>
                              {sport.sport_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="game_description" className="text-white">Description</Label>
                    <Textarea
                      id="game_description"
                      value={formData.game_description}
                      onChange={(e) => handleInputChange('game_description', e.target.value)}
                      placeholder="Enter game description"
                      className="bg-gray-700 border-gray-600 text-white"
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="game_date" className="text-white">Date *</Label>
                      <Input
                        id="game_date"
                        type="date"
                        value={formData.game_date}
                        onChange={(e) => handleInputChange('game_date', e.target.value)}
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="game_time" className="text-white">Time *</Label>
                      <Input
                        id="game_time"
                        type="time"
                        value={formData.game_time}
                        onChange={(e) => handleInputChange('game_time', e.target.value)}
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="field_location" className="text-white">Field Location</Label>
                      <Input
                        id="field_location"
                        value={formData.field_location}
                        onChange={(e) => handleInputChange('field_location', e.target.value)}
                        placeholder="Field/court location"
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="venue_name" className="text-white">Venue Name</Label>
                      <Input
                        id="venue_name"
                        value={formData.venue_name}
                        onChange={(e) => handleInputChange('venue_name', e.target.value)}
                        placeholder="Venue name"
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="venue_address" className="text-white">Venue Address</Label>
                    <Input
                      id="venue_address"
                      value={formData.venue_address}
                      onChange={(e) => handleInputChange('venue_address', e.target.value)}
                      placeholder="Full venue address"
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <Trophy className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>You don't have permission to edit basic game information.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Teams Tab */}
        <TabsContent value="teams" className="space-y-4">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="h-5 w-5" />
                Team Assignment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {userPermissions.canEditTeams ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                        <h4 className="font-medium text-white mb-3">Home Team</h4>
                        <div className="space-y-3">
                          <Select 
                            value={formData.home_team_id?.toString() || 'none'} 
                            onValueChange={(value) => handleInputChange('home_team_id', value === 'none' ? null : parseInt(value))}
                          >
                            <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                              <SelectValue placeholder="Select home team" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No team assigned</SelectItem>
                              {teams.map((team) => (
                                <SelectItem key={team.team_id} value={team.team_id.toString()}>
                                  {team.team_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          
                          <div className="space-y-2">
                            <Label className="text-sm text-gray-300">Team Status</Label>
                            <Select 
                              value={formData.home_team_status} 
                              onValueChange={(value) => handleInputChange('home_team_status', value)}
                            >
                              <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="assigned">Assigned</SelectItem>
                                <SelectItem value="accepted">Accepted</SelectItem>
                                <SelectItem value="declined">Declined</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                        <h4 className="font-medium text-white mb-3">Away Team</h4>
                        <div className="space-y-3">
                          <Select 
                            value={formData.away_team_id?.toString() || 'none'} 
                            onValueChange={(value) => handleInputChange('away_team_id', value === 'none' ? null : parseInt(value))}
                          >
                            <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                              <SelectValue placeholder="Select away team" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No team assigned</SelectItem>
                              {teams.map((team) => (
                                <SelectItem key={team.team_id} value={team.team_id.toString()}>
                                  {team.team_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          
                          <div className="space-y-2">
                            <Label className="text-sm text-gray-300">Team Status</Label>
                            <Select 
                              value={formData.away_team_status} 
                              onValueChange={(value) => handleInputChange('away_team_status', value)}
                            >
                              <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="assigned">Assigned</SelectItem>
                                <SelectItem value="accepted">Accepted</SelectItem>
                                <SelectItem value="declined">Declined</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>You don't have permission to edit team assignments.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Game Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {userPermissions.canEditSettings ? (
                <>
                  {/* Scorekeeper Settings */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-white">Scorekeeper Requirements</h4>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="scorekeeper_needed"
                        checked={formData.scorekeeper_needed}
                        onCheckedChange={(checked) => handleInputChange('scorekeeper_needed', checked)}
                      />
                      <Label htmlFor="scorekeeper_needed" className="text-gray-300">Scorekeeper needed for this game</Label>
                    </div>
                    
                    {formData.scorekeeper_needed && (
                      <div className="space-y-2">
                        <Label htmlFor="scorekeeper_points" className="text-white">Scorekeeper Points</Label>
                        <Input
                          id="scorekeeper_points"
                          type="number"
                          min="0"
                          value={formData.scorekeeper_points}
                          onChange={(e) => handleInputChange('scorekeeper_points', parseInt(e.target.value) || 0)}
                          className="bg-gray-700 border-gray-600 text-white w-32"
                        />
                        <p className="text-sm text-gray-400">Points awarded to the scorekeeper</p>
                      </div>
                    )}
                  </div>

                  {/* Game Status (Admin only) */}
                  {userPermissions.canEditApprovalStatus && (
                    <div className="space-y-4 pt-4 border-t border-gray-600">
                      <h4 className="font-medium text-white">Game Status (Admin Only)</h4>
                      <div className="space-y-2">
                        <Label htmlFor="game_status" className="text-white">Status</Label>
                        <Select 
                          value={formData.game_status} 
                          onValueChange={(value) => handleInputChange('game_status', value)}
                        >
                          <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="approved">Approved</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                            <SelectItem value="confirmed">Confirmed</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                            <SelectItem value="forfeited">Forfeited</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <Settings className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>You don't have permission to edit game settings.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
